import {
  copyObj,
  getSafeUrl,
  useAppDispatch,
  useAppSelector,
  useGetVmStatusQuery,
  useGetInstanceExtraInfoQuery,
  useLazyGetInstanceAccessUrlQuery,
  useSingleRegionLoginQuery,
  userPermissionCheck,
  useGetUserPermissionQuery,
  useGetInstByAccountQuery
} from '@monorepo/shared';
import {
  selectIsRedirectChecked,
  selectRedirectAccount,
  selectSystem,
  updateCurrentAccount,
  updateIsRedirectChecked
} from '@monorepo/shared/store/accountSlice';
import { useCallback, useEffect, useState } from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext
} from 'react-router-dom';
import { Loader, Message, useToaster } from 'rsuite';
import {
  findRedirectNode,
  selectOU,
  updateSelected
} from '@monorepo/shared/store/fcOuSlice';
import { selectDebugKey } from '@monorepo/shared/store/authSlice';
import { HomeElms, server } from '../config/const';
import { redirectVmUrl } from '@monorepo/shared/components/InstanceActions';

const RedirectAccount = () => {
  const { redirect_obj } = useOutletContext<{
    redirect_obj: Record<string, any>;
  }>();
  const { state } = useLocation();
  const debugKey = useAppSelector(selectDebugKey);
  const systemData = useAppSelector(selectSystem);
  const debug_key = state?.debug_key;
  const requestAccount = useAppSelector(selectRedirectAccount);
  const isRedirectChecked = useAppSelector(selectIsRedirectChecked);
  const [isRedirectValid, setIsRedirectValid] = useState<boolean>(
    !!redirect_obj?.account_id
  );
  const [accountRegion, setAccountRegion] = useState<Record<string, any>>({});
  const ouData = useAppSelector(selectOU);
  const dispatch = useAppDispatch();
  const [getInstanceUrl] = useLazyGetInstanceAccessUrlQuery();
  const [message, setMessage] = useState<string>('');
  const navigate = useNavigate();
  const regionLogin = useSingleRegionLoginQuery(
    { region: accountRegion },
    {
      skip: !accountRegion?.globalToken
    }
  );
  const permission = useGetUserPermissionQuery(
    {
      account_node_id: requestAccount?.account_node_id,
      account_id: requestAccount?.account_id,
      user_id: requestAccount?.user_id,
      access_from_user_id: requestAccount?.access_from?.user_id,
      access_from_account_id: requestAccount?.access_from?.account_id,
      server: server
    },
    {
      skip: !requestAccount?.access_from || requestAccount?.portals
    }
  );
  const vmStatus = useGetVmStatusQuery(
    {
      account: {
        ...requestAccount,
        region: {
          url: accountRegion.url || '',
          token: regionLogin?.data?.token || ''
        }
      }
    },
    {
      skip:
        !regionLogin?.isSuccess ||
        !requestAccount?.account_id ||
        (!requestAccount?.portals && !permission?.isSuccess)
    }
  );

  const instanceInfo = useGetInstByAccountQuery(
    {
      account: {
        ...requestAccount,
        region: {
          url: accountRegion.url || '',
          token: regionLogin?.data?.token || ''
        }
      }
    },
    {
      skip:
        !regionLogin?.isSuccess ||
        !requestAccount?.account_id ||
        (!requestAccount?.portals && !permission?.isSuccess)
    }
  );

  const extraInfo = useGetInstanceExtraInfoQuery(
    {
      account: {
        ...requestAccount,
        region: {
          url: accountRegion.url || '',
          token: regionLogin?.data?.token || ''
        }
      }
    },
    {
      skip: !(
        regionLogin?.isSuccess &&
        requestAccount?.is_external &&
        vmStatus?.isSuccess &&
        vmStatus?.data?.is_active
      )
    }
  );

  const doRedirect = useCallback(() => {
    console.info(
      '------------ Fetch Access URL to Do Redirect',
      requestAccount
    );
    getInstanceUrl({
      account: {
        ...requestAccount,
        region: { ...requestAccount.region, ...regionLogin.data }
      }
    })
      .unwrap()
      .then((data) => {
        if (data?.url) {
          const isRedirectSuccess = redirectVmUrl(data, '_self');
          if (!isRedirectSuccess) {
            setMessage('Redirect failed. Your browser blocked the redirect.');
            setIsRedirectValid(false);
            dispatch(updateIsRedirectChecked(true));
          }
        } else if (data !== undefined) {
          setMessage(
            'Fetch redirect account access URL failed: ' +
              requestAccount?.account_id +
              JSON.stringify(data)
          );
          setIsRedirectValid(false);
          dispatch(updateIsRedirectChecked(true));
        }
      })
      .catch((error) => {
        setMessage(
          'Fetch redirect account access URL failed: ' +
            requestAccount?.account_id +
            JSON.stringify(error)
        );
        setIsRedirectValid(false);
        dispatch(updateIsRedirectChecked(true));
      });
  }, [requestAccount, regionLogin]);

  useEffect(() => {
    const accountId = requestAccount?.account_id;

    if (!accountId || accountId <= 0) {
      setIsRedirectValid(false);
      dispatch(updateIsRedirectChecked(true));
      return;
    }

    if (!requestAccount.portals && !permission.isSuccess) return;

    const mergedAccount = requestAccount.portals
      ? requestAccount
      : { ...requestAccount, ...(permission.data || {}) };
    const hasPermission = userPermissionCheck(mergedAccount, HomeElms?.fcName);

    if (!hasPermission) {
      if (mergedAccount?.user_auth_url) {
        window.open(getSafeUrl(mergedAccount.user_auth_url), '_self');
      } else {
        setIsRedirectValid(false);
        navigate('/cred/no-permission');
      }

      return;
    }

    setIsRedirectValid(true);

    const redirectNode = findRedirectNode(ouData, redirect_obj);

    if (redirectNode) {
      dispatch(updateSelected(copyObj(redirectNode)));
    }

    dispatch(updateCurrentAccount(requestAccount));

    const canRedirectToRegion =
      requestAccount?.region?.globalToken &&
      requestAccount?.region?.url &&
      (!requestAccount?.need_force_upgrade ||
        requestAccount?.is_support_gui_upgrade);

    if (canRedirectToRegion) {
      setAccountRegion({
        url: requestAccount.region.url,
        globalToken: requestAccount.region.globalToken
      });
    } else {
      dispatch(updateIsRedirectChecked(true));
      setIsRedirectValid(false);
    }
  }, [
    requestAccount,
    ouData,
    HomeElms?.fcName,
    navigate,
    dispatch,
    permission.isSuccess
  ]);

  useEffect(() => {
    if (regionLogin?.isError) {
      setMessage('Region login failed: ' + JSON.stringify(regionLogin?.error));
      dispatch(updateIsRedirectChecked(true));
      setIsRedirectValid(false);
    }
  }, [regionLogin]);

  useEffect(() => {
    // guard: wait until both API calls finish
    if (!vmStatus.isSuccess || !instanceInfo.isSuccess) return;

    const isActive = vmStatus.data?.is_active;
    const isFsocBlocked =
      instanceInfo.data?.data?.fsoc_onboard &&
      systemData?.disable_access_fsoc_onboarded_instance;

    const isExternal = requestAccount?.is_external;

    if (isActive && !isFsocBlocked) {
      if (!isExternal) {
        doRedirect();
      }
      return;
    }

    // invalid redirect case
    const message = `Redirect check failed, isActive: ${isActive}, isFsocBlocked: ${isFsocBlocked}, account_id: ${requestAccount?.account_id}`;
    setMessage(message);
    setIsRedirectValid(false);
    dispatch(updateIsRedirectChecked(true));
  }, [
    vmStatus.isSuccess,
    vmStatus.isError,
    vmStatus.data,
    instanceInfo.isSuccess,
    instanceInfo.data,
    systemData?.disable_access_fsoc_onboarded_instance,
    requestAccount?.is_external,
    requestAccount?.account_id
  ]);

  useEffect(() => {
    if (extraInfo.isSuccess) {
      const extraData = extraInfo.data;
      if (
        !extraData?.is_adom &&
        extraData?.need_force_upgrade &&
        !extraData?.is_support_gui_upgrade
      ) {
        setIsRedirectValid(false);
        dispatch(updateIsRedirectChecked(true));
      } else {
        doRedirect();
      }
    } else if (extraInfo.isError) {
      setMessage('fetch VM extraInfo failed: ' + extraInfo.error);
      setIsRedirectValid(false);
      dispatch(updateIsRedirectChecked(true));
    }
  }, [extraInfo]);

  useEffect(() => {
    if (message) {
      const errMessage = (
        <Message
          showIcon
          type="warning"
          closable
          header="Redirect Account Failed!"
        >
          <span>
            <b>{`${message}`}</b>
          </span>
        </Message>
      );
      // toaster.push(errMessage, {
      //   placement: 'bottomEnd',
      //   duration: 10000
      // });
      console.info(
        '================== Redirect Error Message ==============',
        message
      );
    }
  }, [message]);

  return isRedirectValid && !isRedirectChecked ? (
    <Loader center content="Please wait while redirecting..." />
  ) : (
    <Outlet
      context={{
        isRedirectValid: isRedirectValid
      }}
    />
  );
};

export default RedirectAccount;
